export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`API ${status}`);
  }
}

function readCookie(name: string): string | null {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const m = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return m ? decodeURIComponent(m[1]!) : null;
}

async function handle(res: Response): Promise<unknown> {
  const text = await res.text();
  const isJson = res.headers.get('content-type')?.includes('application/json') ?? false;
  const body = text && isJson ? JSON.parse(text) : text;
  if (!res.ok) throw new ApiError(res.status, body);
  return body;
}

export async function apiGet<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'include' });
  return handle(res) as Promise<T>;
}

function withCsrf(method: string, body?: unknown): RequestInit {
  const csrf = readCookie('__Host-csrf') ?? '';
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      'x-csrf-token': csrf,
    },
  };
  // Conditionally set body — exactOptionalPropertyTypes forbids assigning
  // `undefined` to RequestInit['body'] (which is `BodyInit | null`).
  if (body !== undefined) init.body = JSON.stringify(body);
  return init;
}

export async function apiPost<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, withCsrf('POST', body));
  return handle(res) as Promise<T>;
}

export async function apiPut<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, withCsrf('PUT', body));
  return handle(res) as Promise<T>;
}

export async function apiPatch<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, withCsrf('PATCH', body));
  return handle(res) as Promise<T>;
}

export async function apiDelete<T>(url: string): Promise<T> {
  const res = await fetch(url, withCsrf('DELETE'));
  return handle(res) as Promise<T>;
}
